import * as React from 'react'
import type { DescService } from '@bufbuild/protobuf'
import type { Client, Interceptor } from '@connectrpc/connect'
import { Code, ConnectError, createClient } from '@connectrpc/connect'
import { createConnectTransport } from '@connectrpc/connect-web'
import { QueryClient } from '@tanstack/react-query'

import { getToken } from '@admin/auth/firebase'
import { apiHost } from '@admin/config'

// トークンが取れないリクエストを送信前に打ち切ってよいか。
// アプリ本体（root.tsxのuseAuthListener）がFirebaseの認証監視を始めた時点でtrueになる。
// Storybookとvitestは認証監視を張らずFirebaseのセッションも持たない（getToken()が常にnull）ため、
// falseのまま従来どおりヘッダー無しで送り、MSWのハンドラーへ到達させる。
let authTokenRequired = false

/**
 * 認証監視が始まったことをtransportに伝える。`@admin/auth/auth` のuseAuthListenerから呼ぶ。
 */
export const setAuthTokenRequired = () => {
  authTokenRequired = true
}

const authInterceptor: Interceptor = next => async req => {
  req.header.set('Accept', 'application/json')
  req.header.set('Content-Type', 'application/json')

  const token = await getToken()
  if (!token) {
    if (authTokenRequired) {
      // 未ログイン・サインアウト済み・トークン失効のまま送っても401になるだけなので、
      // 送信前に打ち切る（従来はヘッダー無しで送っており、失効後も全RPCが401で失敗し続けていた）。
      // 未認証レイアウト（_unAuth配下のログイン画面）から呼ぶRPCは無いため、除外は設けていない。
      throw new ConnectError('unauthenticated', Code.Unauthenticated)
    }
    return await next(req)
  }
  // リクエストヘッダーにトークンをセットする
  req.header.set('Authorization', `Bearer ${token}`)
  return await next(req)
}

export const transport = createConnectTransport({
  baseUrl: apiHost,
  interceptors: [authInterceptor],
  useHttpGet: true,
})
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

export function useClient<T extends DescService>(service: T): Client<T> {
  return React.useMemo(() => createClient(service, transport), [service])
}

export const cacheOptions = {
  // 10分はキャッシュする
  staleTime: 600000,
}
